import React, { useContext, useMemo } from 'react';
import { Card, Button, InputNumber } from 'antd';
import { useSelector } from 'react-redux';
import { TraineeOptionsContext } from '../TraineeOptionsContext';
import {
  BUY,
  SELL,
  CALL_OPTION,
  PUT_OPTION,
  selectors,
  OPTION_CONTRACTS_TYPES,
} from 'state';
import { formatters } from 'services';
import OrderSummary from './OptionsOrderSummary';
import { getSelectedOptionsContract } from '../../../state/app/selectors';

interface OptionsTradeViewProps {
  asset_id: number;
  expiration: string;
}

const OptionsTradeView: React.FC<OptionsTradeViewProps> = ({
  asset_id,
  expiration,
}) => {
  const {
    // selectedOptionsContract,
    selectedOptionType,
    selectedOptionTransaction,
    selectedContractsQuantity,
    setSelectedOptionType,
    setSelectedOptionTransaction,
    setSelectedContractsQuantity,
  } = useContext(TraineeOptionsContext);

  const selectedOptionsContract = useSelector(
    selectors.getSelectedOptionsContract
  );

  // Load assets & scenario date
  const optionsAsset = useSelector(selectors.underlyingAssetById(asset_id));
  const underlyingAsset = useSelector(
    selectors.underlyingAssetById(optionsAsset.non_derivate_asset)
  );
  const { scenario_date } = useSelector(selectors.activeSession);

  // Days to expiry
  const diffDays = useMemo(() => {
    const scen = new Date(scenario_date);
    const exp = new Date(expiration);
    return Math.ceil((exp.getTime() - scen.getTime()) / (1000 * 60 * 60 * 24));
  }, [scenario_date, expiration]);

  // Pick call vs put contract
  const tradedContract = useMemo(() => {
    if (!selectedOptionsContract) return undefined;
    return selectedOptionType === PUT_OPTION
      ? selectedOptionsContract.put
      : selectedOptionsContract.call;
  }, [selectedOptionsContract, selectedOptionType]);

  // Table styling
  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 8px',
  };
  const labelCellStyle: React.CSSProperties = { width: '30%', fontWeight: 500 };
  const valueCellStyle: React.CSSProperties = { width: '70%' };

  return (
    <Card title="Trade" size="small" style={{ marginTop: 20 }}>
      <table style={tableStyle}>
        <tbody>
          <tr>
            <td style={labelCellStyle}>Underlying:</td>
            <td style={valueCellStyle}>{underlyingAsset.ticker}</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Maturity:</td>
            <td style={valueCellStyle}>{diffDays} days</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Strike:</td>
            <td style={valueCellStyle}>{selectedOptionsContract?.strike}</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Type:</td>
            <td style={valueCellStyle}>
              <Button.Group size="small">
                <Button
                  ghost={selectedOptionType !== CALL_OPTION}
                  type={selectedOptionType === CALL_OPTION ? 'primary' : 'default'}
                  onClick={() => setSelectedOptionType(CALL_OPTION)}
                >
                  Call
                </Button>
                <Button
                  ghost={selectedOptionType !== PUT_OPTION}
                  type={selectedOptionType === PUT_OPTION ? 'primary' : 'default'}
                  onClick={() => setSelectedOptionType(PUT_OPTION)}
                >
                  Put
                </Button>
              </Button.Group>
            </td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Contract:</td>
            <td style={valueCellStyle}>
              {underlyingAsset.ticker}_{diffDays}DAY_
              {OPTION_CONTRACTS_TYPES[selectedOptionType]}_
              {selectedOptionsContract?.strike}
            </td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Txn:</td>
            <td style={valueCellStyle}>
              <Button.Group size="small">
                <Button
                  ghost={selectedOptionTransaction !== BUY}
                  type={
                    selectedOptionTransaction === BUY ? 'primary' : 'default'
                  }
                  onClick={() => setSelectedOptionTransaction(BUY)}
                >
                  Buy
                </Button>
                <Button
                  ghost={selectedOptionTransaction !== SELL}
                  type={
                    selectedOptionTransaction === SELL ? 'primary' : 'default'
                  }
                  onClick={() => setSelectedOptionTransaction(SELL)}
                >
                  Sell
                </Button>
              </Button.Group>
            </td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Price:</td>
            <td style={valueCellStyle}>
              {tradedContract?.bid ?? '-'} / {tradedContract?.ask ?? '-'}
            </td>
          </tr>
          <tr>
            <td style={labelCellStyle}>
              {OPTION_CONTRACTS_TYPES[selectedOptionType]} IV:
            </td>
            <td style={valueCellStyle}>
              {tradedContract
                ? formatters.percent2Digit(tradedContract.implied_volatility)
                : '-'}
            </td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Delta:</td>
            <td style={valueCellStyle}>{tradedContract?.delta ?? '-'}</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Gamma:</td>
            <td style={valueCellStyle}>{tradedContract?.gamma ?? '-'}</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Vega:</td>
            <td style={valueCellStyle}>{tradedContract?.vega ?? '-'}</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Theta:</td>
            <td style={valueCellStyle}>{tradedContract?.theta ?? '-'}</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Contracts:</td>
            <td style={valueCellStyle}>
              <InputNumber
                min={0}
                value={selectedContractsQuantity}
                formatter={formatters.commasNoDigits}
                onChange={val => setSelectedContractsQuantity(Number(val || 0))}
                style={{ width: 120 }}
              />
            </td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Equity Equivalent:</td>
            <td style={valueCellStyle}>
              <InputNumber
                disabled
                value={selectedContractsQuantity * 100}
                formatter={formatters.commasNoDigits}
                style={{ width: 120 }}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

export default OptionsTradeView;
